import { SessionApiOrigin } from "../Constants";

export type SessionGrant = {
  accessToken: string;
  expiresIn: number;
  userId: string;
};

export type SessionFailure =
  | { kind: "rejected"; status: number }
  | { kind: "network" };

export class SessionError extends Error {
  readonly failure: SessionFailure;

  constructor(failure: SessionFailure) {
    super(
      failure.kind === "rejected"
        ? `session request rejected with ${failure.status}`
        : "session request failed",
    );
    this.name = "SessionError";
    this.failure = failure;
  }
}

export const isSignedOut = (err: unknown): boolean =>
  err instanceof SessionError &&
  err.failure.kind === "rejected" &&
  err.failure.status === 401;

const post = async (path: string, body: object): Promise<Response> => {
  let response: Response;
  try {
    response = await fetch(`${SessionApiOrigin}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SessionError({ kind: "network" });
  }
  if (!response.ok) {
    throw new SessionError({ kind: "rejected", status: response.status });
  }
  return response;
};

type GrantBody = { access_token: string; expires_in: number; user_id: string };

const isGrantBody = (data: unknown): data is GrantBody =>
  typeof data === "object" &&
  data !== null &&
  "access_token" in data &&
  typeof data.access_token === "string" &&
  "expires_in" in data &&
  typeof data.expires_in === "number" &&
  "user_id" in data &&
  typeof data.user_id === "string";

const grantFrom = async (response: Response): Promise<SessionGrant> => {
  const data: unknown = await response.json();
  if (!isGrantBody(data)) throw new Error("malformed session grant");
  return {
    accessToken: data.access_token,
    expiresIn: data.expires_in,
    userId: data.user_id,
  };
};

export const establishSession = (refreshToken: string): Promise<SessionGrant> =>
  post("/v2/auth/session", { refresh_token: refreshToken }).then(grantFrom);

export const currentAccessToken = (): Promise<SessionGrant> =>
  post("/v2/auth/session/token", {}).then(grantFrom);

export const signOut = async (): Promise<void> => {
  await post("/v2/auth/session/logout", {});
};
